// Port of cringe-old/PipeGame/flow1.js (a "Flow Free" clone): connect the dots of the same colour
// without crossing; the board is solved when every cell is part of a finished pipe.
// It drives the DOM of `grid` directly, like the original; returns a cleanup function.

// The level the PHP game always loaded (8x8).
export const LEVEL = '2b7gfe5d10d1f3b1g2e1c2ca1h3a5h5';

const OPPOSITE = { t: 'b', b: 't', l: 'r', r: 'l' };

function loadLevel(grid, s) {
  const data = [];
  while (s.length) {
    s = s.replace(/^\d+|[a-z]/i, (x) => {
      if (parseInt(x, 10)) {
        for (let n = parseInt(x, 10); n > 0; n--) data.push(0);
      } else {
        data.push(parseInt(x, 36) - 9);
      }
      return '';
    });
  }
  const size = Math.sqrt(data.length);
  if (size !== Math.floor(size)) throw new Error('Invalid grid definition.');
  grid.setAttribute('data-size', size);
  grid.style.setProperty('--size', size);
  grid.innerHTML = '';
  data.forEach((n, i) => {
    const block = document.createElement('div');
    if (n) {
      block.setAttribute('data-id', n);
      block.setAttribute('data-point', 'true');
    }
    block.setAttribute('data-i', i);
    grid.appendChild(block);
  });
  return size;
}

export function initPipeGame(grid, onSolvedChange) {
  const size = loadLevel(grid, LEVEL);
  const cells = Array.from(grid.children);
  let currentColor = null;
  let currentBlock = null;
  let lastBlock = null;
  let currentPath = {};
  let tracing = false;

  const isNeighbour = (i, j) => {
    const x = (i % size) - (j % size);
    const y = Math.floor(i / size) - Math.floor(j / size);
    if (x === -1 && y === 0) return 'l';
    if (x === 1 && y === 0) return 'r';
    if (x === 0 && y === -1) return 't';
    if (x === 0 && y === 1) return 'b';
    return false;
  };
  const idx = (block) => parseInt(block.getAttribute('data-i'), 10);

  const cleanAll = (items, limit = 1) => {
    if (!items.length) return [];
    const colorId = items[0].getAttribute('data-id');
    grid.querySelectorAll(`div[data-id="${colorId}"]`).forEach((b) => b.removeAttribute('data-completed'));
    while (items.length > limit) {
      const block = items.pop();
      const previous = items[items.length - 1];
      const direction = isNeighbour(idx(previous), idx(block));
      if (block.getAttribute('data-point') !== 'true') block.removeAttribute('data-id');
      block.removeAttribute(`data-${direction}`);
      previous.removeAttribute(`data-${OPPOSITE[direction]}`);
    }
    if (items.length === 1) items.pop();
    return items;
  };

  const cellFrom = (event) => {
    let el = event.target;
    if (event.type.startsWith('touch')) {
      const t = event.touches[0] || event.changedTouches[0];
      el = t ? document.elementFromPoint(t.clientX, t.clientY) : null;
    }
    return el && el.parentNode === grid ? el : null;
  };

  const down = (event) => {
    const block = cellFrom(event);
    if (!block) return;
    const colorId = parseInt(block.getAttribute('data-id'), 10);
    if (!colorId) return;
    if (event.type.startsWith('mouse') && event.button !== 0) return;

    currentColor = colorId;
    if (currentBlock !== block) {
      lastBlock = currentBlock;
      currentBlock = block;
      if (block.getAttribute('data-point') === 'true') {
        currentPath[currentColor] = currentPath[currentColor] ? cleanAll(currentPath[currentColor]) : [];
      } else {
        const pathIndex = (currentPath[currentColor] || []).indexOf(block);
        if (pathIndex > -1) {
          currentPath[currentColor] = cleanAll(currentPath[currentColor], pathIndex + 1);
          lastBlock = currentPath[currentColor][currentPath[currentColor].length - 1];
        }
      }
      if (!currentPath[currentColor]) currentPath[currentColor] = [];
      currentPath[currentColor].push(currentBlock);
    }
    tracing = true;
    event.preventDefault();
  };

  const move = (event) => {
    if (!tracing || !currentBlock) return;
    const newBlock = cellFrom(event);
    if (!newBlock) return;
    event.preventDefault();
    if (newBlock === currentBlock) return;
    const direction = isNeighbour(idx(currentBlock), idx(newBlock));
    if (!direction) return;

    const newColor = newBlock.getAttribute('data-id');
    const isPoint = newBlock.getAttribute('data-point') === 'true';
    const path = currentPath[currentColor];

    if (newColor == currentColor) { // eslint-disable-line eqeqeq
      const pathIndex = path.indexOf(newBlock);
      if (pathIndex > -1) {
        // Dragging back over the pipe shortens it.
        currentPath[currentColor] = cleanAll(path, pathIndex + 1);
        currentBlock = currentPath[currentColor][currentPath[currentColor].length - 1];
        lastBlock = currentPath[currentColor][currentPath[currentColor].length - 2];
      } else if (isPoint) {
        // Reached the matching dot: the pipe is complete.
        lastBlock = currentBlock;
        currentBlock = newBlock;
        path.push(currentBlock);
        path.forEach((b) => b.setAttribute('data-completed', 'true'));
        currentBlock.setAttribute('data-id', currentColor);
        currentBlock.setAttribute(`data-${direction}`, '');
        lastBlock.setAttribute(`data-${OPPOSITE[direction]}`, '');
      }
    } else if (!newColor) {
      // Empty cell: extend the pipe, unless it already reached its other dot.
      if (currentBlock.getAttribute('data-point') && path.length > 1) return;
      lastBlock = currentBlock;
      currentBlock = newBlock;
      currentBlock.setAttribute('data-id', currentColor);
      currentBlock.setAttribute(`data-${direction}`, '');
      lastBlock.setAttribute(`data-${OPPOSITE[direction]}`, '');
      path.push(currentBlock);
    }
  };

  const up = (event) => {
    if (!tracing) return;
    tracing = false;
    const solved = cells.every((c) => c.getAttribute('data-completed') === 'true');
    onSolvedChange(solved);
    if (event.cancelable) event.preventDefault();
  };

  grid.addEventListener('mousedown', down);
  grid.addEventListener('mousemove', move);
  window.addEventListener('mouseup', up);
  grid.addEventListener('touchstart', down, { passive: false });
  grid.addEventListener('touchmove', move, { passive: false });
  window.addEventListener('touchend', up);

  return () => {
    grid.removeEventListener('mousedown', down);
    grid.removeEventListener('mousemove', move);
    window.removeEventListener('mouseup', up);
    grid.removeEventListener('touchstart', down);
    grid.removeEventListener('touchmove', move);
    window.removeEventListener('touchend', up);
    currentPath = {};
    grid.innerHTML = '';
  };
}
